import { useState } from 'react';
import type { BowProfile } from '../types';
import { BOW_LIBRARY } from '../../lib/bowLibrary';
import { SIGHTS } from '@/data/sights';

interface BowProfileFormProps {
  profile: BowProfile;
  mode: 'create' | 'edit';
  onSave: (p: BowProfile) => void;
  onCancel: () => void;
}

export function BowProfileForm({ profile, mode, onSave, onCancel }: BowProfileFormProps) {
  const [draft, setDraft] = useState<BowProfile>(profile);

  function update(patch: Partial<BowProfile>) {
    setDraft({ ...draft, ...patch });
  }
  function updateBow(patch: Partial<BowProfile['bow']>) {
    setDraft({ ...draft, bow: { ...draft.bow, ...patch } });
  }
  function updateSight(patch: Partial<BowProfile['sight']>) {
    setDraft({ ...draft, sight: { ...draft.sight, ...patch } });
  }

  function applyBowLibrarySelection(brand: string, model: string) {
    const lib = BOW_LIBRARY.find((b) => b.brand === brand && b.model === model);
    if (!lib) return;
    updateBow({
      brand,
      model,
      iboFps: lib.iboFps,
      drawWeightLb: Math.min(Math.max(draft.bow.drawWeightLb, lib.drawWeightLb.min), lib.drawWeightLb.max),
      drawLengthIn: Math.min(Math.max(draft.bow.drawLengthIn, lib.drawLengthIn.min), lib.drawLengthIn.max),
    });
  }

  // Sight picker — canonical sight catalog (same list as the builder's
  // Bow Setup section), grouped by brand. Stored as "Brand Model", matching
  // what forgeLibraryImport writes when hydrating main-Forge setups.
  const sightNames = new Set(SIGHTS.map((s) => `${s.brand} ${s.model}`));
  const sightBrands = Array.from(new Set(SIGHTS.map((s) => s.brand))).sort();
  const sightsByBrand = (brand: string) =>
    SIGHTS.filter((s) => s.brand === brand).map((s) => `${s.brand} ${s.model}`);
  // Saved profiles may carry a pre-2026-06-10 housing-library name — keep it
  // selectable so editing a profile doesn't silently wipe the field.
  const legacySightName =
    draft.sight.housing && !sightNames.has(draft.sight.housing) ? draft.sight.housing : null;
  // Pin diameters: the selected sight's published options when known,
  // otherwise the standard ladder. Always include the current value so an
  // existing profile never renders with an empty select.
  const selectedSight = SIGHTS.find((s) => `${s.brand} ${s.model}` === draft.sight.housing);
  const pinDiameterOptions = Array.from(
    new Set([...(selectedSight?.pinDiameters ?? [0.010, 0.015, 0.019, 0.029]), draft.sight.pinDiameterIn]),
  ).filter((d) => d > 0).sort((a, b) => a - b);

  const bowBrands = Array.from(new Set(BOW_LIBRARY.map((b) => b.brand))).sort();
  const bowModelsForBrand = draft.bow.brand
    ? BOW_LIBRARY.filter((b) => b.brand === draft.bow.brand)
    : [];

  return (
    <form
      className="profile-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (!draft.name.trim()) return;
        onSave(draft);
      }}
    >
      <h2>{mode === 'create' ? 'Add a bow' : `Edit ${profile.name}`}</h2>

      <section className="form-section">
        <h3>Profile</h3>
        <label>
          Name
          <input
            type="text"
            value={draft.name}
            placeholder="e.g. Mach 33 western"
            required
            onChange={(e) => update({ name: e.target.value })}
          />
        </label>
        <label className="checkbox">
          <input
            type="checkbox"
            checked={draft.isPrimary}
            onChange={(e) => update({ isPrimary: e.target.checked })}
          />
          Mark as primary rig (defaults across the Forge)
        </label>
      </section>

      <section className="form-section">
        <h3>Bow</h3>
        <div className="form-grid">
          <label>
            Brand
            <select
              value={draft.bow.brand ?? ''}
              onChange={(e) => updateBow({ brand: e.target.value, model: '' })}
            >
              <option value="">— Select —</option>
              {bowBrands.map((b) => (
                <option key={b} value={b}>{b}</option>
              ))}
            </select>
          </label>
          <label>
            Model
            <select
              value={draft.bow.model ?? ''}
              onChange={(e) => applyBowLibrarySelection(draft.bow.brand ?? '', e.target.value)}
              disabled={!draft.bow.brand}
            >
              <option value="">— Select —</option>
              {bowModelsForBrand.map((b) => (
                <option key={b.model} value={b.model}>
                  {b.model} {b.status === 'recent-legacy' ? '(legacy)' : ''}
                </option>
              ))}
            </select>
          </label>
          <label>
            IBO fps
            <input
              type="number"
              value={draft.bow.iboFps}
              min={280}
              max={360}
              onChange={(e) => updateBow({ iboFps: Number(e.target.value) })}
            />
          </label>
          <label>
            Draw weight (lb)
            <input
              type="number"
              value={draft.bow.drawWeightLb}
              min={30}
              max={90}
              step={1}
              onChange={(e) => updateBow({ drawWeightLb: Number(e.target.value) })}
            />
          </label>
          <label>
            Draw length (in)
            <input
              type="number"
              value={draft.bow.drawLengthIn}
              min={24}
              max={32}
              step={0.25}
              onChange={(e) => updateBow({ drawLengthIn: Number(e.target.value) })}
            />
          </label>
          <label>
            Chrono fps (optional)
            <input
              type="number"
              value={draft.bow.measuredVelocityFps ?? ''}
              min={200}
              max={400}
              placeholder="auto from IBO"
              onChange={(e) => {
                const v = e.target.value === '' ? undefined : Number(e.target.value);
                updateBow({ measuredVelocityFps: v });
              }}
            />
          </label>
        </div>
      </section>

      <section className="form-section">
        <h3>Sight</h3>
        <div className="form-grid">
          <label className="span-2">
            Sight
            <select
              value={draft.sight.housing}
              onChange={(e) => updateSight({ housing: e.target.value })}
            >
              <option value="">— Select —</option>
              {legacySightName && (
                <option value={legacySightName}>{legacySightName} (legacy)</option>
              )}
              {sightBrands.map((brand) => (
                <optgroup key={brand} label={brand}>
                  {sightsByBrand(brand).map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </label>
          <label>
            Pin diameter (in)
            <select
              value={draft.sight.pinDiameterIn}
              onChange={(e) => updateSight({ pinDiameterIn: Number(e.target.value) })}
            >
              {pinDiameterOptions.map((d) => (
                <option key={d} value={d}>{`.${String(d).split('.')[1].padEnd(3, '0')}″`}</option>
              ))}
            </select>
          </label>
          <label>
            Pin count
            <input
              type="number"
              value={draft.sight.pinCount ?? 1}
              min={1}
              max={7}
              onChange={(e) => updateSight({ pinCount: Number(e.target.value) })}
            />
          </label>
        </div>
      </section>

      <div className="form-actions">
        <button type="submit" className="primary-cta">
          {mode === 'create' ? 'Save bow' : 'Save changes'}
        </button>
        <button type="button" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}
